import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CameraCapture } from "@/components/CameraCapture";
import { addPending, countPending, flushPending } from "@/lib/offlineQueue";
import { compressImage } from "@/lib/imageCompress";
import { UPLOAD } from "@/constants/appIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, X, Camera, Loader2, ImagePlus, WifiOff } from "lucide-react";

export default function Upload() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const inputRef = useRef();
  const [outlet, setOutlet] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const n = await flushPending();
      if (n > 0) qc.invalidateQueries({ queryKey: ["dashboard"] });
      if (mounted) setPendingCount(await countPending());
    })();
    const onOnline = async () => {
      const n = await flushPending();
      if (n > 0) { toast.success(`${n} nota offline berhasil diunggah.`); qc.invalidateQueries({ queryKey: ["dashboard"] }); }
      setPendingCount(await countPending());
    };
    window.addEventListener("online", onOnline);
    return () => { mounted = false; window.removeEventListener("online", onOnline); };
  }, [qc]);

  const { data: outlets = [] } = useQuery({
    queryKey: ["outlets"],
    queryFn: async () => (await api.get("/outlets")).data,
  });

  const addFiles = async (list) => {
    const arr = await Promise.all(Array.from(list).map((f) => compressImage(f)));
    setFiles((f) => [...f, ...arr]);
    setPreviews((p) => [...p, ...arr.map((f) => URL.createObjectURL(f))]);
  };

  const removeFile = (i) => {
    setFiles((f) => f.filter((_, idx) => idx !== i));
    setPreviews((p) => p.filter((_, idx) => idx !== i));
  };

  const enqueueOffline = async () => {
    await addPending({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      outlet_id: outlet, files, created_at: new Date().toISOString(),
    });
    setPendingCount(await countPending());
    setFiles([]); setPreviews([]);
    toast.success("Tidak ada koneksi. Foto & draf disimpan offline, akan diunggah otomatis saat online kembali.");
  };

  const submit = async () => {
    if (!outlet) { toast.error("Pilih outlet dulu."); return; }
    if (files.length === 0) { toast.error("Tambahkan minimal satu foto nota."); return; }
    if (!navigator.onLine) { await enqueueOffline(); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("outlet_id", outlet);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/receipts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto & draf tersimpan. AI sedang membaca nota…");
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      nav(`/review/${data.id}`);
    } catch (e) {
      if (!e.response) { await enqueueOffline(); }
      else { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mengunggah"); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 nh-fade">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Unggah Nota</h1>
        <p className="text-sm text-muted-foreground">
          Pilih outlet, lalu foto/unggah nota. Beberapa foto boleh digabung sebagai satu nota panjang.
        </p>
      </div>

      {pendingCount > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2" data-testid="offline-pending">
          <WifiOff className="w-4 h-4 shrink-0" /> {pendingCount} nota tersimpan offline — akan terunggah otomatis saat koneksi kembali.
        </div>
      )}

      <Card className="p-5 space-y-5">
        <div>
          <Label>Outlet</Label>
          <Select value={outlet} onValueChange={setOutlet}>
            <SelectTrigger data-testid={UPLOAD.outletSelect} className="mt-1.5">
              <SelectValue placeholder="Pilih outlet" />
            </SelectTrigger>
            <SelectContent>
              {outlets.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Foto Nota</Label>
          <Button type="button" variant="default" onClick={() => setCameraOpen(true)} data-testid="open-camera"
            className="w-full mt-1.5 mb-2 h-12 rounded-lg text-base">
            <Camera className="w-5 h-5 mr-2" /> Buka Kamera
          </Button>
          <div
            data-testid={UPLOAD.dropzone}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            className="mt-1.5 border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-secondary/50 transition-colors"
          >
            <UploadCloud className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="mt-2 text-sm font-medium">Ketuk untuk memotret atau pilih foto</p>
            <p className="text-xs text-muted-foreground">Nota cetak & tulisan tangan · JPG/PNG</p>
            <input ref={inputRef} data-testid={UPLOAD.fileInput} type="file" accept="image/*"
              capture="environment" multiple className="hidden"
              onChange={(e) => addFiles(e.target.files)} />
          </div>
        </div>

        {previews.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {previews.map((src, i) => (
              <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
                <img src={src} alt={`nota-${i}`} className="w-full h-full object-cover" />
                <button onClick={() => removeFile(i)}
                  className="absolute top-1 right-1 bg-black/60 rounded-full p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3 h-3" />
                </button>
                <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 rounded">#{i + 1}</span>
              </div>
            ))}
            <button onClick={() => inputRef.current?.click()}
              className="aspect-square rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-primary/50">
              <ImagePlus className="w-6 h-6" />
            </button>
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={submit} disabled={busy} data-testid={UPLOAD.submit} className="rounded-full flex-1 h-11">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-2" /> Simpan & Baca dengan AI</>}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Foto & draf disimpan lebih dulu sebelum AI dipanggil. Jika AI penuh/limit, data tetap tersimpan dan bisa diisi manual.
        </p>
      </Card>

      <CameraCapture open={cameraOpen} onClose={() => setCameraOpen(false)}
        onCapture={(file) => { addFiles([file]); setCameraOpen(false); toast.success("Foto ditambahkan. Bisa foto lagi atau simpan."); }} />
    </div>
  );
}
